<style lang="scss">
  .select-colored.success,
  .select-colored.warning {
    .v-select__selections {
      color: black !important;
    }
  }
  .select-colored.v-select {
    &.v-select--is-menu-active > .v-input__slot,
    &.v-input--is-focused > .v-input__slot {
      border-radius: inherit !important;
    }

    .v-input__slot {
      min-height: 40px;
      height: 40px;
    }

    .v-select__slot {
      justify-content: center;
    }

    &.v-text-field--outlined fieldset {
      border-width: 0px;
    }

    .v-select__selections {
      flex: 0 0 auto;
      width: initial;

      input {
        margin: 0px;
        max-width: 0px;
        min-width: 0px;
        width: 0px;
      }
    }

    .v-select__selection--comma {
      max-width: inherit;
      margin-right: 0px;
    }

    .v-input__append-inner {
      flex-shrink: initial;
      margin-top: 8px;
      margin-left: 0px;
      padding: 0;
    }
  }

  .select-colored-content .v-list {
    padding-top: 0px;
    padding-bottom: 0px;
  }
</style>
<template>
  <div>
    <v-select
      :background-color="color"
      class="select-colored font-weight-bold text-caption editStatus"
      :class="color"
      dark
      hide-details
      :menu-props="{ maxHeight: 400, contentClass: 'select-colored-content', offsetY: true }"
      :items="items"
      rounded
      outlined
      :value="selectedStatus"
      @input="onInput"
      :disabled="loading"
      @focus="onStatusFocus"
      @blur="onStatusBlur"
      @keydown="onStatusKeyDown"
      ref="statusSelector"
    >
      <template #prepend-item>
        <v-subheader class="text-uppercase">Status</v-subheader>
      </template>
      <template #item="{ item: { color, text }, on, attrs }">
        <v-list-item v-on="on" v-bind="attrs" class="align-stretch pl-0">
          <v-sheet class="mr-4" :color="color" width="8px"></v-sheet>
          <v-list-item-content>
            <v-list-item-title>{{ text }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </template>
    </v-select>
    <confirm-publish-dialog
      v-if="publishProgramModal"
      v-model="publishProgramModal"
      :title="autoPublishLiterals.dialog.publish.title"
      :message="autoPublishLiterals.dialog.publish.message"
      :program="program"
      @confirm="onPublishComplete"
      @cancel="onPublishCancel"
    />
  </div>
</template>
<script lang="ts">
  import Vue from 'vue';
  import { DigitalProgram } from '@/store/programs/types';
  import { mapActions } from 'vuex';
  import { PROGRAMS_STATUS_COLORS, PROGRAMS_STATUS_OPTIONS, AUTO_PUBLISH_LITERALS } from '@/constants/programs';
  import { ConfirmPublishDialog } from '.';

  export default Vue.extend({
    components: {
      ConfirmPublishDialog,
    },
    props: {
      program: {
        type: DigitalProgram,
        required: true,
      },
      value: {
        type: Number,
        required: true,
      },
    },
    data() {
      return {
        selectedStatus: this.value,
        isMenuActive: false, // Track if the menu is activ
        loading: false,
        prevKey: '',
        publishProgramModal: false,
        pendingStatus: null as number | null,
      };
    },
    watch: {
      value(status: number) {
        this.selectedStatus = status;
        this.$emit('input', this.selectedStatus);
      },
    },
    computed: {
      autoPublishLiterals() {
        return AUTO_PUBLISH_LITERALS;
      },
      items() {
        return PROGRAMS_STATUS_OPTIONS;
      },
      color(): string | undefined {
        return PROGRAMS_STATUS_COLORS[this.value];
      },
      publishedStatusValue(): number {
        return PROGRAMS_STATUS_OPTIONS.find((o) => o.text === 'Published')?.value || 0;
      },
    },
    methods: {
      onPublish() {
        this.publishProgramModal = true;
      },
      onPublishComplete() {
        this.publishProgramModal = false;
      },
      onPublishCancel() {
        this.publishProgramModal = false;
        this.selectedStatus = this.value;
        this.pendingStatus = null;
      },
      onStatusFocus() {
        (this.$refs.statusSelector as any).isMenuActive = true;
      },
      onStatusBlur() {
        (this.$refs.statusSelector as any).isMenuActive = false;
      },
      onStatusKeyDown(evt: any) {
        evt.preventDefault();
        const activeElement = document.activeElement;
        const focusableElementsAction = [...document.querySelectorAll('.editStatus input[readonly]')];
        const focusableElementsEdit = [...document.querySelectorAll('a.editAction')];
        const focusableElementsMenu = [...document.querySelectorAll('button.actionMenu')];
        // Find the index of the currently focused element
        const currentIndex = focusableElementsAction.indexOf(activeElement);
        // Calculate the next index
        // Focus the next element
        if (focusableElementsEdit[currentIndex] && evt.key == 'Tab' && !evt.shiftKey) {
          (focusableElementsEdit[currentIndex] as HTMLInputElement).focus();
        } else if (evt.key == 'Tab' && evt.shiftKey) {
          if (currentIndex == 0) document.getElementById('keyword-filter').focus();
          else (focusableElementsMenu[currentIndex - 1] as HTMLInputElement).focus();
        }
        this.prevKey = evt.key;
      },
      ...mapActions('programs', { changeStatus: 'changeStatus' }),
      ...mapActions('ui', { showToast: 'showToast' }),
      async updateStatus(status: number) {
        const oldStatus = this.value;
        try {
          this.loading = true;
          await this.changeStatus({
            id: this.program.id,
            oldStatus,
            status,
          });
          this.selectedStatus = status;
          this.$emit('input', status);
        } catch (error) {
          const msg = error.response?.data || 'Could not change program status';
          this.showToast(msg);
          // Revert on error
          this.selectedStatus = oldStatus;
        } finally {
          this.loading = false;
        }
      },
      async onInput(status: number) {
        // If selecting "Published", show confirmation dialog
        if (status === this.publishedStatusValue) {
          this.pendingStatus = status;
          this.selectedStatus = status;
          this.onPublish();
        } else {
          // For other statuses, update immediately
          await this.updateStatus(status);
        }
      },
    },
  });
</script>
